import { useCallback, useState } from 'react';
import { validateForm } from '../validation/rules.js';

/**
 * Form state + validation.
 *
 * A field's error only appears once the user has left it (`touched`) or has
 * tried to submit. Nobody wants "Name is required" shouted at them before they
 * have typed the first letter.
 *
 * `applyServerErrors(details)` takes the `details` object from a 422 and shows
 * it against the right inputs, so a rule the client does not know about still
 * lands on the correct field.
 */
export default function useForm(initialValues, schema) {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const setValue = useCallback((field, value) => {
    setValues((current) => ({ ...current, [field]: value }));
    // Clear the error as soon as the user starts fixing it.
    setErrors((current) => (current[field] ? { ...current, [field]: '' } : current));
  }, []);

  const handleChange = useCallback(
    (field) => (event) => setValue(field, event?.target ? event.target.value : event),
    [setValue]
  );

  const handleBlur = useCallback(
    (field) => () => {
      setTouched((current) => ({ ...current, [field]: true }));
      setValues((current) => {
        const message = validateForm(current, { [field]: schema[field] })[field] || '';
        setErrors((prev) => ({ ...prev, [field]: message }));
        return current;
      });
    },
    [schema]
  );

  /** Everything a controlled input needs, including the error and its aria wiring. */
  const fieldProps = useCallback(
    (field) => {
      const show = Boolean(touched[field] || submitted);
      const error = show ? errors[field] || '' : '';
      return {
        name: field,
        value: values[field] ?? '',
        onChange: handleChange(field),
        onBlur: handleBlur(field),
        error,
        required: Boolean(schema[field]?.required),
      };
    },
    [values, errors, touched, submitted, schema, handleChange, handleBlur]
  );

  const validate = useCallback(() => {
    const found = validateForm(values, schema);
    setErrors(found);
    setSubmitted(true);
    return Object.keys(found).length === 0;
  }, [values, schema]);

  /** Paint the server's field errors onto the form. Returns true if any landed. */
  const applyServerErrors = useCallback((details) => {
    if (!details || typeof details !== 'object') return false;
    const known = Object.entries(details).filter(([field]) => field in initialValues);
    if (!known.length) return false;
    setSubmitted(true);
    setErrors((current) => ({ ...current, ...Object.fromEntries(known) }));
    return true;
  }, [initialValues]);

  const reset = useCallback((next = initialValues) => {
    setValues(next);
    setErrors({});
    setTouched({});
    setSubmitted(false);
  }, [initialValues]);

  const fill = useCallback((next) => {
    setValues(next);
    setErrors({});
    setTouched({});
    setSubmitted(false);
  }, []);

  return { values, errors, touched, submitted, setValue, setValues, fieldProps, validate, applyServerErrors, reset, fill };
}
